import React from "react";
import COLORS from "../../constants/colors";
import FlowTopBar from "../common/FlowTopBar";
import HeartView from "../common/HeartView";
import { wordApi } from "../../utils/api";
import {
  btnBarStyle,
  centerSubStyle,
  exampleBoxStyle,
  heroWordStyle,
  hintBtnStyle,
  knowBtnStyle,
  mainCardStyle,
  pageStyle,
} from "../../styles/commonStyles";
import { speakFrench } from "../../utils/speech";

// 复用高亮单词函数
function highlightWord(example, word) {
  if (!example || !word) return example || "";
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return example.replace(new RegExp(escaped, "gi"), `<strong>${word}</strong>`);
}

//  新增：CSS 动画样式
const animationStyles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }
  
  @keyframes shimmer {
    0% {
      background-position: -200% center;
    }
    100% {
      background-position: 200% center;
    }
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }
  
  .review-word-enter {
    animation: fadeInUp 0.6s ease-out;
  }
  
  .review-card-hover {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .review-card-hover:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
  }
  
  .review-btn-known {
    background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
    transition: all 0.3s ease;
  }
  
  .review-btn-known:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(82, 196, 26, 0.3);
  }
  
  .review-btn-fuzzy {
    background: linear-gradient(135deg, #fa8c16 0%, #ffa940 100%);
    transition: all 0.3s ease;
  }
  
  .review-btn-fuzzy:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(250, 140, 22, 0.3);
  }
  
  .review-btn-unknown {
    background: linear-gradient(135deg, #f5222d 0%, #ff4d4f 100%);
    transition: all 0.3s ease;
  }
  
  .review-btn-unknown:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(245, 34, 45, 0.3);
  }
  
  .review-hint-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transition: all 0.3s ease;
  }
  
  .review-hint-btn:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  }
  
  .review-image-container {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
  }
  
  .review-image-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    animation: shimmer 2s infinite;
  }
  
  .review-progress-bar {
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    height: 4px;
    border-radius: 2px;
    transition: width 0.3s ease;
  }
`;

export default function ReviewPhaseOne({
  word,
  progressText,
  heartCount,
  onKnown,
  onPrev,
  onStar,
  onSkip,
  onHome,
  disablePrev = false,
}) {
  // 保留提示阶段状态（和 LearnPhaseTwo 完全一致）
  const [hintStage, setHintStage] = React.useState(0);
  // 保留选中状态（原有 ReviewPhaseOne 的逻辑）
  const [selected, setSelected] = React.useState(null);
  // 🔥 修改：仅保留加载状态，移除手动显示状态
  const [imageLoading, setImageLoading] = React.useState(false);
  const [wordImageUrl, setWordImageUrl] = React.useState(null);

  // 🔥 新增：当单词变化时，加载 AI 生成的图片
  React.useEffect(() => {
    if (word?.id && !word.image) {
      // 尝试获取 AI 生成的图片
      loadWordImage(word.id);
    } else if (word?.image) {
      // 如果单词已有图片，直接使用
      setWordImageUrl(word.image);
    } else {
      setWordImageUrl(null);
    }
  }, [word?.id]);

  // 🔥 新增：加载单词图片的函数
  const loadWordImage = async (wordId) => {
    try {
      setImageLoading(true);
      const resp = await wordApi.generateWordImage(wordId);
      if (resp && resp.image_url) {
        setWordImageUrl(resp.image_url);
      }
    } catch (err) {
      console.warn("加载 AI 图片失败：", err);
      setWordImageUrl(null);
    } finally {
      setImageLoading(false);
    }
  };

  // 处理提示点击逻辑（完全照搬 LearnPhaseTwo）
  const handleHintClick = () => {
    if (hintStage === 0) {
      setHintStage(1);
    } else if (hintStage === 1) {
      setHintStage(2);
    }
  };

  // 处理认识/模糊/不认识点击逻辑（兼容原有延迟回调）
  const handleKnowClick = (status) => {
    if (selected) return;
    setSelected(status);

    setTimeout(() => {
      if (typeof onKnown === 'function') {
        onKnown(status); // 'known' | 'fuzzy' | 'unknown'
      }
    }, 1000);
  };

  // 🔥 修改后的图片展示函数（图片缩小，保持完整显示）
  function getWordImageBlock(word) {
    // 如果图片正在加载或已加载，显示图片
    if (imageLoading || wordImageUrl) {
      return (
        <div
          className="review-image-container"
          style={{
            width: "100%",
            maxHeight: "200px",
            borderRadius: "16px",
            marginBottom: "1rem",
            overflow: "hidden",
            position: "relative",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
            animation: "fadeInUp 0.4s ease-out",
            background: "#f5f5f5",
          }}
        >
          {wordImageUrl && (
            <img
              src={wordImageUrl}
              alt={word.word}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "200px",
                objectFit: "contain",
                display: "block",
                transition: "transform 0.3s ease",
              }}
              onError={(e) => {
                // 图片加载失败，隐藏整个图片区域
                e.target.parentElement.style.display = "none";
              }}
            />
          )}
        </div>
      );
    }

    // 图片未加载时，返回空的div（隐藏）
    return null;
  }

  // 空值保护
  if (!word) return null;

  return (
    <div style={{
      ...pageStyle(),
      background: "linear-gradient(135deg, #e8ecf1 0%, #d4d8dd 100%)",
      height: "100vh",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "1.5rem",
    }}>
      {/* 注入动画样式 */}
      <style>{animationStyles}</style>
      
      <div className="review-word-enter" style={{
        ...mainCardStyle(),
        background: "rgba(255, 255, 255, 0.98)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 12px 48px rgba(0, 0, 0, 0.15)",
        borderRadius: "24px",
        border: "1px solid rgba(255, 255, 255, 0.9)",
        width: "100%",
        maxWidth: "800px",
        maxHeight: "calc(100vh - 3rem)",
        overflowY: "auto",
        overflowX: "hidden",
        padding: "2rem",
      }}>
        {/* 进度条 */}
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: "#f0f0f0",
          borderRadius: "20px 20px 0 0",
          overflow: "hidden",
        }}>
          <div 
            className="review-progress-bar"
            style={{ 
              width: `${(parseInt(progressText.split('/')[0]) / parseInt(progressText.split('/')[1])) * 100}%` 
            }}
          />
        </div>

        {/* 顶部导航（完全照搬 FlowTopBar） */}
        <div style={{ marginTop: "0" }}>
          <FlowTopBar
            title={`学习进度：${progressText}`}
            onPrev={onPrev}
            onStar={onStar}
            onSkip={onSkip}
            onHome={onHome}
            disablePrev={disablePrev}
          />
        </div>

        {/* 单词 + 发音（大字号，无衬线视觉；右侧显示已知 emoji） */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: 12, 
          marginTop: '0.3rem',
          marginBottom: '0.5rem',
          animation: "fadeInUp 0.4s ease-out 0.1s both",
        }}>
          <div style={{ textAlign: "center", cursor: "default" }}>
            <div style={{
              ...heroWordStyle(),
              fontSize: "3rem",
              fontWeight: 800,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: "-0.02em",
            }}>{word.word}</div>
            <div style={{ 
              ...centerSubStyle(), 
              marginTop: "0.3rem", 
              color: COLORS.subtext, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 8,
              fontSize: "1.1rem",
            }}>
              <button 
                onClick={() => speakFrench(word.word)} 
                style={{ 
                  border: 'none', 
                  background: 'transparent', 
                  cursor: 'pointer', 
                  fontSize: 16,
                  transition: "transform 0.2s ease",
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.2)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
              >
                🔊
              </button>
              <span style={{ fontWeight: 500 }}>{word.phonetic}</span>
            </div>
          </div>

          {/* 认识时的小表情 */}
          <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {selected === 'known' && (
              <div style={{ 
                fontSize: 20, 
                animation: "pulse 0.6s ease-out",
              }}>
                ❤️
              </div>
            )}
          </div>
        </div>

        {/* 修改：自动显示图片，无需点击 */}
        <div style={{ animation: "fadeInUp 0.4s ease-out 0.2s both" }}>
          {getWordImageBlock(word)}
        </div>

        {/* 法语提示面板 */}
        <div
          style={{
            width: "100%",
            minHeight: "50px",
            borderRadius: "12px",
            border: "2px solid rgba(102, 126, 234, 0.2)",
            marginBottom: "0.5rem",
            background: "linear-gradient(135deg, #f8f9ff 0%, #fff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0.5rem",
            animation: hintStage >= 1 ? "fadeInUp 0.3s ease-out" : "none",
            boxShadow: hintStage >= 1 ? "0 2px 8px rgba(102, 126, 234, 0.1)" : "none",
          }}
        >
          {hintStage >= 1 && (
            <span style={{ 
              color: "#333", 
              fontSize: "1rem", 
              fontWeight: 600,
              textAlign: "center",
            }}>
              {word.frenchHint || `Définition: ${word.word}`}
            </span>
          )}
          {hintStage === 0 && (
            <span style={{ color: "#999", fontSize: "0.85rem" }}>
              点击提示按钮查看法语释义
            </span>
          )}
        </div>
        
        {/* 汉语提示面板 */}
        <div
          style={{
            width: "100%",
            minHeight: "50px",
            borderRadius: "12px",
            border: "2px solid rgba(250, 140, 22, 0.2)",
            marginBottom: "0.5rem",
            background: "linear-gradient(135deg, #fff8f0 0%, #fff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0.5rem",
            animation: hintStage >= 2 ? "fadeInUp 0.3s ease-out" : "none",
            boxShadow: hintStage >= 2 ? "0 2px 8px rgba(250, 140, 22, 0.1)" : "none",
          }}
        >
          {hintStage >= 2 && (
            <span style={{ 
              color: "#fa8c16", 
              fontSize: "1.05rem", 
              fontWeight: 700,
              textAlign: "center",
            }}>
              {word.translation || "暂无中文释义"}
            </span>
          )}
          {hintStage < 2 && (
            <span style={{ color: "#999", fontSize: "0.85rem" }}>
              再次点击提示查看中文释义
            </span>
          )}
        </div>

        {/* 例句面板 */}
        <div style={{
          ...exampleBoxStyle(),
          borderRadius: "12px",
          border: "2px solid rgba(82, 196, 26, 0.15)",
          background: "linear-gradient(135deg, #f6ffed 0%, #fff 100%)",
          padding: "0.6rem",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          animation: "fadeInUp 0.4s ease-out 0.4s both",
          marginBottom: "0.5rem",
        }}>
          <span
            dangerouslySetInnerHTML={{
              __html: highlightWord(word.example, word.word),
            }}
            style={{
              fontSize: "0.95rem",
              lineHeight: "1.5",
            }}
          />
          {hintStage >= 2 && (
            <div style={{ 
              marginTop: "0.5rem", 
              color: "#666",
              fontSize: "0.85rem",
              paddingTop: "0.5rem",
              borderTop: "1px solid rgba(0, 0, 0, 0.06)",
            }}>
              {word.exampleTranslation}
            </div>
          )}
        </div>

        {/* 提示按钮 */}
        <div style={{ 
          textAlign: "center", 
          margin: "0.4rem 0",
          animation: "fadeInUp 0.4s ease-out 0.5s both",
        }}>
          <button 
            onClick={handleHintClick} 
            className="review-hint-btn"
            style={{
              border: "none",
              borderRadius: "12px",
              padding: "0.5rem 1.5rem",
              color: "white",
              fontWeight: 600,
              fontSize: "0.9rem",
              cursor: "pointer",
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "2px",
              boxShadow: "0 3px 10px rgba(102, 126, 234, 0.3)",
            }}
          >
            <span style={{ fontSize: "1.1rem" }}></span>
            <span>
              {hintStage === 0 ? "提示一下" : hintStage === 1 ? "显示中文意思" : "已显示全部提示"}
            </span>
          </button>
        </div>

        {/* 操作按钮 */}
        <div style={{ 
          display: "flex", 
          gap: "0.6rem", 
          justifyContent: 'center',
          animation: "fadeInUp 0.4s ease-out 0.6s both",
        }}>
          <button 
            onClick={() => handleKnowClick('known')} 
            className="review-btn-known"
            style={{
              border: "none",
              borderRadius: "12px",
              padding: "0.7rem 1.2rem",
              color: "white",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: selected ? 'not-allowed' : 'pointer',
              opacity: selected === 'known' ? 0.7 : 1,
              pointerEvents: selected ? 'none' : 'auto',
              flex: 1,
              maxWidth: "130px",
              boxShadow: "0 3px 10px rgba(82, 196, 26, 0.25)",
            }}
          >
            认识
            <div style={{ 
              height: "2px", 
              background: "rgba(255,255,255,0.5)", 
              borderRadius: "2px",
              marginTop: "3px",
            }} />
          </button>
          <button 
            onClick={() => handleKnowClick('fuzzy')} 
            className="review-btn-fuzzy"
            style={{
              border: "none",
              borderRadius: "12px",
              padding: "0.7rem 1.2rem",
              color: "white",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: selected ? 'not-allowed' : 'pointer',
              opacity: selected === 'fuzzy' ? 0.7 : 1,
              pointerEvents: selected ? 'none' : 'auto',
              flex: 1,
              maxWidth: "130px",
              boxShadow: "0 3px 10px rgba(250, 140, 22, 0.25)",
            }}
          >
            模糊
            <div style={{ 
              height: "2px", 
              background: "rgba(255,255,255,0.5)", 
              borderRadius: "2px",
              marginTop: "3px",
            }} />
          </button>
          <button 
            onClick={() => handleKnowClick('unknown')} 
            className="review-btn-unknown"
            style={{
              border: "none",
              borderRadius: "12px",
              padding: "0.7rem 1.2rem",
              color: "white",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: selected ? 'not-allowed' : 'pointer',
              opacity: selected === 'unknown' ? 0.7 : 1,
              pointerEvents: selected ? 'none' : 'auto',
              flex: 1,
              maxWidth: "130px",
              boxShadow: "0 3px 10px rgba(245, 34, 45, 0.25)",
            }}
          >
            不认识
            <div style={{ 
              height: "2px", 
              background: "rgba(255,255,255,0.5)", 
              borderRadius: "2px",
              marginTop: "3px",
            }} />
          </button>
        </div>
      </div>
    </div>
  );
}